import { nanoid } from "nanoid";
import pluralize from "pluralize";
import { useContext, useMemo } from "react";
import { Disclosure, DisclosureGroupStateContext, DisclosurePanel } from "react-aria-components";
import { HStack, Icons, ItemTile, SymbolIcon, TagIntent, TriggerButton, TriggerButtonSize, VStack } from "widgets";

import type { Organism } from "@/graphql.ts";

const SELECTED_GENOME_TILES_DISCLOSURE = "selected-genome-tiles-disclosure";

interface SelectedOrganismTilesProps {
  organisms: Pick<Organism, "id" | "name" | "hidden" | "isVirtual">[];
  isLoading: boolean;
  onClearSelection?: (genomeId: Organism["id"]) => void;
  disabled?: boolean;
}

export const SelectedGenomesTiles = ({
  organisms,
  isLoading,
  onClearSelection,
  disabled = false,
}: SelectedOrganismTilesProps) => {
  const count = useMemo(() => organisms.length, [organisms]);
  const disclosureGroupState = useContext(DisclosureGroupStateContext);

  if (isLoading) {
    return <OrganismsTileSkeleton />;
  }

  if (count === 0) {
    return (
      <ItemTile
        name="No Genomes"
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        id={"no-genome"}
        isDisabled
      />
    );
  }

  if (count === 1) {
    return <GenomeTile genome={organisms[0]} disabled={disabled} onClearSelection={onClearSelection} />;
  }

  return (
    <Disclosure id={SELECTED_GENOME_TILES_DISCLOSURE}>
      {({ isExpanded }) => (
        <VStack style={{ paddingTop: "3px" }} spacing={3}>
          <HStack>
            <ItemTile
              name={`${count} ${pluralize("genome", count)}`}
              icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
              id={"placeholder-tile"}
              key={"placeholder-tile"}
            />
            <TriggerButton
              slot="trigger"
              icon={isExpanded ? "unfold_less" : "unfold_more"}
              label={count ? `${count} ${pluralize("genome", count)}` : "No genomes"}
              badge={isExpanded ? undefined : String(count)}
              badgeIntent="secondary"
              size={TriggerButtonSize.Small}
              isDisabled={count === 1}
              onPress={() =>
                isExpanded
                  ? disclosureGroupState?.expandedKeys.add(SELECTED_GENOME_TILES_DISCLOSURE)
                  : disclosureGroupState?.expandedKeys.delete(SELECTED_GENOME_TILES_DISCLOSURE)
              }
            />
          </HStack>
          <DisclosurePanel>
            <VStack spacing={3}>
              {organisms.map((genome) => (
                <GenomeTile
                  genome={genome}
                  onClearSelection={onClearSelection}
                  disabled={disabled}
                  key={`genome-${genome.id}-tile`}
                />
              ))}
            </VStack>
          </DisclosurePanel>
        </VStack>
      )}
    </Disclosure>
  );
};

const GenomeTile = ({
  genome,
  onClearSelection,
  disabled,
}: {
  genome: Pick<Organism, "id" | "name" | "hidden" | "isVirtual">;
  onClearSelection?: (genomeId: Organism["id"]) => void;
  disabled: boolean;
}) => {
  return genome.hidden ? (
    <ItemTile
      key={genome.id}
      name="Restricted Genome"
      icon={<SymbolIcon icon="security" size={20} />}
      id={`hidden genome-${nanoid()}`}
      isDisabled
    />
  ) : (
    <ItemTile
      key={genome.id}
      name={genome.name ?? "Unknown Genome"}
      icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
      id={genome.id}
      action={<RemoveGenomeButton genomeId={genome.id} onClearSelection={onClearSelection} disabled={disabled} />}
      itemTags={genome.isVirtual ? [{ name: "Virtual", color: TagIntent.Warning }] : []}
    />
  );
};

const OrganismsTileSkeleton = () => (
  <ItemTile
    name="Loading..."
    icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
    id={"loading"}
    isDisabled
  />
);

const RemoveGenomeButton = ({
  onClearSelection,
  genomeId,
  disabled,
}: {
  onClearSelection: SelectedOrganismTilesProps["onClearSelection"];
  genomeId: Organism["id"];
  disabled: boolean;
}) => {
  return onClearSelection != undefined ? (
    <TriggerButton
      icon={"cancel"}
      label={"Remove"}
      onPress={() => {
        onClearSelection(genomeId);
      }}
      key={`Remove-button-organism-${genomeId}`}
      isDisabled={disabled}
    />
  ) : undefined;
};
